import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import MargineBox from "../components/MargineBox";
import { useMarginParams, calcMargine, canSeeMarginDetails, costoStimato } from "@/lib/margine";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Download, ChevronRight, ChevronLeft, Check, Sparkles, FileText, Send, AlertTriangle, ShieldCheck, Clock, Plus,
} from "lucide-react";
import { fmtEuro, fmtNum } from "../editor/utils";
import jsPDF from "jspdf";
import { InfissoQuickConfigurator } from "../components/InfissoQuickConfigurator";
import ListinoProdottoPicker from "../components/ListinoProdottoPicker";
import ModalitaPagamentoPicker from "../components/ModalitaPagamentoPicker";
import { useAuth } from "@/contexts/AuthContext";
import { SwapControl, ExcludedSuggestions, ScorporoBanner, scorporoInfo, swapItem, restoreItem } from "../components/preventivo/VociIntercambiabili";

const SOGLIA_SCONTO_AUTO = 5; // Venditore può applicare fino a 5% senza autorizzazione

export default function PreventivoPacchetto() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const vediDettagliMargine = canSeeMarginDetails(user);
  const marginParams = useMarginParams(isAdmin ? "semplice" : (user?.venditore_level || "semplice"));

  const [step, setStep] = useState(0); // 0 package, 1 mq, 2 items, 3 optional, 4 bagno, 5 cliente, 6 result
  const [packages, setPackages] = useState([]);
  const [optionals, setOptionals] = useState([]);
  const [bathroomTiers, setBathroomTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scontoReq, setScontoReq] = useState(null); // richiesta sconto attiva (pending/approvato/rifiutato)
  const [scontoDialog, setScontoDialog] = useState(false);
  const [scontoForm, setScontoForm] = useState({ pct: 10, motivo: "" });
  const [listinoPickerOpen, setListinoPickerOpen] = useState(false);
  // Sub-picker per sostituire un prodotto già incluso nel pacchetto
  const [substituteDialog, setSubstituteDialog] = useState(null); // { pkgItem, products }
  const [allProdottiFornitori, setAllProdottiFornitori] = useState([]);
  const [vociMap, setVociMap] = useState({});

  const [prev, setPrev] = useState({
    package_id: null,
    mq: 70,
    items: [],       // [{id, name, category, unit, qty_richiesta, included_qty, unit_price, unit_consigliato}]
    optional: [],    // [{id, name, qty, unit_price, total, per_m2, unit}]
    bathroom_tier: null, // legacy: kept for backward compat (singolo bagno)
    bathroom_surcharge: 0, // legacy
    bathrooms: [],   // NEW: [{ id, tier_id, included, surcharge_eur }] — array bagni multipli
    listini_selections: [], // Prodotti scelti dai listini fornitori (porte, piastrelle, ecc.)
    cliente: { nome: "", cognome: "", indirizzo: "", email: "", telefono: "" },
    note: "",
    sconto_pct: 0,
    iva_pct: 10,
  });
  const [numero, setNumero] = useState(null);
  const [stato, setStato] = useState("bozza");

  // Prefill ref: sopravvive a re-mount (StrictMode dev). Applichiamo extras una sola volta.
  const prefillRef = useRef({ data: null, applied: false, loaded: false });
  const [infissiModalOpen, setInfissiModalOpen] = useState(false);
  const [extraFreeOpen, setExtraFreeOpen] = useState(false);
  const [extraFreeForm, setExtraFreeForm] = useState({ name: "", category: "EXTRA", unit: "pz", qty: 1, unit_price: 0, note: "" });

  const onInfissiConfirm = ({ items, totale }) => {
    const newRows = items.map((it, i) => ({
      id: `infisso-${Date.now()}-${i}`,
      voce_id: `infisso-${Date.now()}-${i}`,
      name: `${it.tipologia_name || "Infisso"} ${it.larghezza}×${it.altezza}cm · ${it.materiale_name} ${it.vetro_name} (${it.ante} ante)${it.tapparella ? " + tapparella" : ""}${it.zanzariera ? " + zanzariera" : ""}`,
      category: "EXTRA",
      unit: "pz",
      qty_mode: "fissa", qty_ratio: 0, qty_value: it.qty || 1,
      unit_price: Math.round((it.price || 0) / (it.qty || 1)),
      included_qty: 0,
      qty_richiesta: it.qty || 1,
      from_infissi: true,
      infisso_meta: it,
    }));
    setPrev((p) => ({ ...p, items: [...(p.items || []), ...newRows] }));
    toast.success(`${items.length} infissi aggiunti come extra (${fmtEuro(totale)})`);
  };

  useEffect(() => {
    (async () => {
      try {
        const [pk, op, bt, prod] = await Promise.all([
          api.get("/packages"), api.get("/packages/optional"), api.get("/packages/bathroom-tiers"),
          api.get("/fornitori-listini-prodotti/cerca?max_results=2000").catch(() => ({ data: [] })),
        ]);
        api.get("/voci-backoffice").then((r) => setVociMap(Object.fromEntries((r.data || []).map((v) => [v.id, v])))).catch(() => {});
        const pkgsSorted = (pk.data || []).slice().sort((a, b) => (a.price_per_m2 || 0) - (b.price_per_m2 || 0));
        setPackages(pkgsSorted); setOptionals(op.data); setBathroomTiers(bt.data);
        setAllProdottiFornitori(prod.data || []);
        if (!isNew) {
          const { data } = await api.get(`/preventivi/${id}`);
          const silverBasePrice = (bt.data && bt.data[0]?.price) || 0;
          // Backward-compat: se `bathrooms` non esiste, costruiscilo da `bathroom_tier` (singolo legacy)
          let bathrooms = Array.isArray(data.bathrooms) ? data.bathrooms : [];
          if (!bathrooms.length && data.bathroom_tier) {
            const tier = (bt.data || []).find((t) => t.id === data.bathroom_tier);
            bathrooms = [{ id: `bath-${Date.now()}`, tier_id: data.bathroom_tier, included: true, surcharge_eur: tier ? Math.max(0, (tier.price || 0) - silverBasePrice) : 0 }];
          }
          setPrev({
            package_id: data.package_id, mq: data.mq,
            items: data.items || [], optional: data.optional || [],
            bathroom_tier: data.bathroom_tier,
            bathroom_surcharge: data.bathroom_tier ? (((bt.data.find((t) => t.id === data.bathroom_tier)?.price || 0) - silverBasePrice) || 0) : 0,
            bathrooms,
            cliente: data.cliente || {}, note: data.note || "",
            sconto_pct: data.sconto_pct || 0, iva_pct: data.iva_pct || 10,
          });
          setNumero(data.numero); setStato(data.stato);
          setStep(6);
          // Carica richiesta sconto attiva (pending o ultima decisa) per questo preventivo
          try {
            const sr = await api.get(`/sconto-richieste`);
            const rows = (sr.data || []).filter(r => r.preventivo_id === data.id);
            if (rows.length) setScontoReq(rows[0]);
          } catch {}
        } else {
          // Prefill dal configuratore esigenze (sessionStorage + URL ?prefill=1)
          // NON rimuoviamo subito sessionStorage: StrictMode (dev) può causare doppio mount,
          // l'eventuale removeItem nel primo mount cancellerebbe il dato prima del secondo mount.
          // Lo puliamo solo dopo aver salvato il preventivo.
          try {
            const params = new URLSearchParams(window.location.search);
            if (params.get("prefill") === "1" && !prefillRef.current.loaded) {
              const raw = sessionStorage.getItem("preventivo_prefill");
              if (raw) {
                const pf = JSON.parse(raw);
                prefillRef.current = { data: pf, applied: false, loaded: true };
                setPrev((p) => ({
                  ...p,
                  package_id: pf.package_id || null,
                  mq: pf.mq || p.mq,
                  cliente: { ...p.cliente, ...(pf.cliente || {}) },
                  note: pf.note || p.note,
                }));
                if (pf.package_id) setStep(2); // skip package and mq selection
              }
            }
          } catch (e) { /* prefill not available */ }
        }
      } catch (e) {
        console.error("[PreventivoPacchetto load]", e);
        toast.error("Errore caricamento: " + (e?.response?.data?.detail || e?.response?.statusText || e?.message || "verifica la connessione"));
      }
      setLoading(false);
    })();
  }, [id, isNew]);

  // Recompute items when package or mq changes
  useEffect(() => {
    if (!prev.package_id) return;
    const pkg = packages.find((p) => p.id === prev.package_id); if (!pkg) return;
    setPrev((p) => {
      const ml = (p.mq || 0) * 0.4;
      const calcQty = (it, m) => {
        if (it.qty_mode === "fissa") return it.qty_value || 0;
        if (it.qty_mode === "ml") return (it.qty_ratio || 0) * ml;
        return (it.qty_ratio || 0) * m;
      };
      const newItems = (pkg.items || []).map((it) => {
        const included = calcQty(it, p.mq || 0);
        const existing = (p.items || []).find((x) => x.id === it.id);
        return {
          id: it.id, voce_id: it.voce_id || it.id, name: it.name, category: it.category, unit: it.unit,
          qty_mode: it.qty_mode, qty_ratio: it.qty_ratio, qty_value: it.qty_value,
          unit_price: existing && existing.unit_price != null ? existing.unit_price : (it.prezzo_rivendita != null ? it.prezzo_rivendita : (it.unit_price_pkg != null ? it.unit_price_pkg : 0)),
          included_qty: parseFloat(included.toFixed(2)),
          qty_richiesta: existing && Math.abs((existing.qty_richiesta || 0) - (existing.included_qty || 0)) > 0.001 ? existing.qty_richiesta : parseFloat(included.toFixed(2)),
          modificabile_dal_venditore: it.modificabile_dal_venditore !== false, // default true se non specificato
          unit_price_pkg: it.unit_price_pkg, // SOGLIA MAX coperto dal pacchetto (€/unità). Se null → prezzo_rivendita standard
          excluded: existing ? !!existing.excluded : false,
          ...(existing?.sostituzione ? { voce_id: existing.voce_id, name: existing.name, category: existing.category, unit_price: existing.unit_price, unit_price_pkg: null, sostituzione: existing.sostituzione } : {}),
        };
      });
      // Preserve EXTRA rows aggiunte dal configuratore (from_configuratore=true)
      // che non corrispondono a voci del pacchetto corrente
      const pkgVoceIds = new Set(newItems.map((it) => it.voce_id || it.id));
      const preservedExtras = [];
      (p.items || []).forEach((it) => {
        if (it.from_configuratore && !pkgVoceIds.has(it.voce_id || it.id)) {
          preservedExtras.push(it);
        }
      });
      newItems.push(...preservedExtras);
      // Apply prefill extras (UNA SOLA VOLTA: rilevato tramite presenza di item from_configuratore)
      const prefill = prefillRef.current;
      const alreadyApplied = (p.items || []).some((it) => it.from_configuratore);
      if (prefill.data && !alreadyApplied) {
        const extras = prefill.data.extras || [];
        extras.forEach((ex) => {
          const idx = newItems.findIndex((it) => (it.voce_id || it.id) === ex.voce_id);
          if (idx >= 0) {
            // voce già nel pacchetto → aumenta qty_richiesta oltre l'incluso
            newItems[idx] = {
              ...newItems[idx],
              qty_richiesta: parseFloat(((newItems[idx].included_qty || 0) + (ex.qty || 0)).toFixed(2)),
              from_configuratore: true,
            };
          } else {
            // voce non inclusa → aggiungi come riga extra (included_qty=0)
            newItems.push({
              id: ex.voce_id, voce_id: ex.voce_id, name: ex.name, category: "EXTRA", unit: ex.unit || "pz",
              qty_mode: "fissa", qty_ratio: 0, qty_value: ex.qty || 1,
              unit_price: ex.unit_price || 0,
              included_qty: 0,
              qty_richiesta: parseFloat((ex.qty || 1).toFixed(2)),
              from_configuratore: true,
            });
          }
        });
      }
      return { ...p, items: newItems };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prev.package_id, prev.mq, packages]);

  // Auto-init: quando l'utente entra nello step 4 (Bagno) per la prima volta E i tier sono caricati,
  // se la lista bagni è vuota → crea il bagno #1 SILVER incluso (riflette il pacchetto base).
  useEffect(() => {
    if (step === 4 && (prev.bathrooms || []).length === 0 && bathroomTiers.length > 0) {
      setPrev((s) => (s.bathrooms?.length ? s : { ...s, bathrooms: [{ id: `bath-${Date.now()}`, tier_id: bathroomTiers[0].id, included: true }] }));
    }
  }, [step, bathroomTiers, prev.bathrooms]);

  const pkg = packages.find((p) => p.id === prev.package_id);

  // Maggiorazione mq piccole:
  // mq < 40 → modalità "a corpo": equivalente a 40 mq + 15% margine sicurezza
  // 40 ≤ mq < 60 → +10% sul base
  // ≥60 → normale
  const mqAdjustment = useMemo(() => {
    const m = parseFloat(prev.mq || 0);
    if (!m || m <= 0) return { mode: "normal", multiplier: 1, mq_effettivi: m, note: "" };
    if (m < 40) return { mode: "a_corpo", multiplier: 1.15, mq_effettivi: 40, note: `Sotto i 40 m² il preventivo è calcolato A CORPO (forfait fisso): equiparato a 40 m² + 15% di margine sicurezza. È necessario per mantenere la marginalità su lavori piccoli (i costi fissi non scalano col m²).` };
    if (m < 60) return { mode: "maggiorato", multiplier: 1.10, mq_effettivi: m, note: `Sotto i 60 m² i prezzi base sono maggiorati del +10% per coprire i costi fissi di cantiere.` };
    return { mode: "normal", multiplier: 1, mq_effettivi: m, note: "" };
  }, [prev.mq]);

  const totals = useMemo(() => {
    if (!pkg) return { base: 0, extras: 0, optional: 0, bagno: 0, subtotal: 0, sconto: 0, iva: 0, total: 0, mqAdjustment, hasOverride: false };
    // Voci ESCLUSE dal preventivo (rimosse dall'utente): non contano né come base né come extras
    const activeItems = (prev.items || []).filter((it) => !it.excluded);
    // BASE: se l'admin ha definito un PREZZO TOTALE FISSO sul pacchetto (price_override),
    // usa quello e ignora €/mq e maggiorazione mq. Altrimenti applica €/mq × mq_effettivi × multiplier.
    const hasOverride = pkg.price_override != null && Number(pkg.price_override) > 0;
    const base = hasOverride
      ? Number(pkg.price_override)
      : pkg.price_per_m2 * mqAdjustment.mq_effettivi * mqAdjustment.multiplier;
    // OPTIONAL avanzati: calcolo del totale.
    //   - forfait: usa o.total (qty*unit_price impostato dall'admin in price_scontato)
    //   - listino_scontato: prezzo_unitario_listino * (1 - sconto_pct/100) * qty (qty inserita dal venditore)
    const optional = (prev.optional || []).reduce((s, o) => {
      if (o.tipo_prezzo === "listino_scontato" && o.prezzo_unitario_listino) {
        const unit = (o.prezzo_unitario_listino || 0) * (1 - (o.sconto_pct || 0) / 100);
        const qty = parseFloat(o.qty) || 0;
        return s + unit * qty;
      }
      if (o.total != null) return s + (o.total || 0);
      const qty = parseFloat(o.qty) || 1;
      const unit = parseFloat(o.unit_price) || parseFloat(o.price_scontato) || 0;
      return s + unit * qty;
    }, 0);
    // Categorie escluse dagli extras (es. "infissi"): se c'è un optional attivo che include una categoria,
    // gli extras di voci di quella categoria NON vengono conteggiati nel subtotale.
    const excludedExtraCats = new Set();
    (prev.optional || []).forEach(o => {
      (o.exclude_extras_categories || []).forEach(c => excludedExtraCats.add(String(c).toLowerCase()));
    });
    const isExcludedItem = (it) => {
      if (!excludedExtraCats.size) return false;
      const cat = (it.category || "").toLowerCase();
      const name = (it.name || "").toLowerCase();
      for (const c of excludedExtraCats) {
        if (cat.includes(c) || name.includes(c)) return true;
      }
      return false;
    };
    const extras = activeItems.reduce((s, it) => {
      if (isExcludedItem(it)) return s; // voci infissi (o altra categoria) coperte da optional → no extra
      const incl = it.included_qty || 0;
      const reqs = Math.max(0, it.qty_richiesta || 0);
      const unitPrice = Math.max(0, it.unit_price || 0);
      let extraVal = 0;
      if (incl === 0) {
        extraVal = reqs * unitPrice;
      } else {
        const extraQty = Math.max(0, reqs - incl);
        extraVal = extraQty * unitPrice;
        if (it.modificabile_dal_venditore) {
          const soglia = it.unit_price_pkg;
          if (soglia != null && soglia > 0 && unitPrice > soglia) {
            extraVal += (unitPrice - soglia) * incl;
          }
        }
      }
      return s + extraVal;
    }, 0);
    // Calcolo bagni multipli: per ogni bagno incluso → surcharge = max(0, tier.price - silver.price);
    // per ogni bagno extra → costo intero del tier scelto
    const silverBase = (bathroomTiers && bathroomTiers[0]?.price) || 0;
    let bagno = 0;
    const bathroomsList = prev.bathrooms || [];
    if (bathroomsList.length > 0) {
      bathroomsList.forEach((b) => {
        const t = bathroomTiers.find((x) => x.id === b.tier_id);
        if (!t) return;
        if (b.included) {
          bagno += Math.max(0, (t.price || 0) - silverBase);
        } else {
          bagno += (t.price || 0);
        }
      });
    } else {
      // Legacy: usa bathroom_surcharge se array vuoto
      bagno = prev.bathroom_surcharge || 0;
    }
    // Totale finiture da listini fornitori (porte, piastrelle, sanitari, ecc.)
    // = pre-inclusi nel pacchetto (pkg.listini_items) + scelti dal venditore (prev.listini_selections)
    // Se il pacchetto ha price_override, i pre-inclusi sono GIÀ contenuti nel prezzo forfait, non si sommano.
    const listiniPkg = hasOverride ? 0 : ((pkg.listini_items || []).reduce((s, p) => s + ((parseFloat(p.qty) || 0) * (parseFloat(p.prezzo_rivendita) || 0)), 0));
    const listiniSel = (prev.listini_selections || []).reduce((s, p) => s + ((parseFloat(p.qty) || 0) * (parseFloat(p.prezzo_rivendita) || 0)), 0);
    const listini = listiniPkg + listiniSel;
    const subtotal = base + extras + optional + bagno + listini;
    const sconto = subtotal * (prev.sconto_pct || 0) / 100;
    const afterDisc = subtotal - sconto;
    const iva = afterDisc * (prev.iva_pct || 10) / 100;
    const total = afterDisc + iva;
    // Costi diretti reali: q.tà richiesta × prezzo d'acquisto (stima prezzo/1.8 solo dove manca il costo)
    let costoVoci = 0, costoVociStimato = 0;
    activeItems.forEach((it) => {
      if (isExcludedItem(it)) return;
      const q = Math.max(0, it.qty_richiesta || 0);
      const pa = Number(vociMap[it.voce_id]?.prezzo_acquisto ?? it.prezzo_acquisto) || 0;
      if (pa > 0) costoVoci += q * pa; else costoVociStimato += costoStimato(q * (it.unit_price || 0));
    });
    const costoListino = (p) => (parseFloat(p.qty) || 0) * (Number(p.prezzo_netto) || Number(p.prezzo_acquisto) || costoStimato(p.prezzo_rivendita));
    const costoListini = [...(pkg.listini_items || []), ...(prev.listini_selections || [])].reduce((s, p) => s + costoListino(p), 0);
    const costiBreakdown = [
      { nome: "Lavorazioni (voci)", importo: costoVoci },
      { nome: "Lavorazioni senza costo d'acquisto", importo: costoVociStimato, stimato: true },
      { nome: "Finiture listini", importo: costoListini },
      { nome: "Optional", importo: costoStimato(optional), stimato: true },
      { nome: "Bagno", importo: costoStimato(bagno), stimato: true },
    ];
    const costiDiretti = costiBreakdown.reduce((s, b) => s + b.importo, 0);
    return { base, extras, optional, bagno, listini, listiniPkg, listiniSel, subtotal, sconto, iva, total, mqAdjustment, hasOverride, costiDiretti, costiBreakdown };
  }, [prev, pkg, bathroomTiers, mqAdjustment, vociMap]);

  const margine = calcMargine(totals.subtotal - totals.sconto, totals.costiDiretti, marginParams);

  const scorporo = useMemo(() => scorporoInfo(prev.items, vociMap), [prev.items, vociMap]);
  const scorporoMotivo = scorporo.totale ? ` · Voci scorporate: ${scorporo.righe.map((r) => r.name).join(", ")} (sconto a margine invariato ${fmtEuro(scorporo.totale)})` : "";

  // Regole sconto: admin libero; venditore fino a SOGLIA_SCONTO_AUTO e margine stimato ≥30%, oltre serve autorizzazione
  const applySconto = (raw) => {
    const v = Math.max(0, Math.min(100, raw));
    if (isAdmin) { setPrev((s) => ({ ...s, sconto_pct: v })); return; }
    if (v > SOGLIA_SCONTO_AUTO) {
      const capOk = (() => { const s = calcMargine(totals.subtotal * (1 - SOGLIA_SCONTO_AUTO / 100), totals.costiDiretti, marginParams); return !s || s.pct >= s.soglia; })();
      if (capOk) setPrev((s) => ({ ...s, sconto_pct: SOGLIA_SCONTO_AUTO }));
      setScontoForm((sf) => ({ ...sf, pct: v, motivo: `Sconto ${v}%${scorporoMotivo}` }));
      setScontoDialog(true);
      toast.info(`Sconto > ${SOGLIA_SCONTO_AUTO}%: serve autorizzazione admin`);
      return;
    }
    const sim = calcMargine(totals.subtotal * (1 - v / 100), totals.costiDiretti, marginParams);
    const simMarg = sim ? sim.pct : 100;
    const soglia = sim ? sim.soglia : 30;
    if (simMarg < soglia) {
      setScontoForm((sf) => ({ ...sf, pct: v, motivo: `Sconto ${v}%: margine sotto il minimo${scorporoMotivo}` }));
      setScontoDialog(true);
      toast.warning(`Sconto bloccato: il margine scenderebbe sotto il minimo consentito. Serve autorizzazione admin.`);
      return;
    }
    setPrev((s) => ({ ...s, sconto_pct: v }));
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      tipo: "pacchetto",
      cliente: prev.cliente,
      package_id: prev.package_id,
      mq: prev.mq,
      items: prev.items,
      optional: prev.optional,
      bathroom_tier: prev.bathroom_tier, // legacy
      bathrooms: prev.bathrooms || [],   // nuovo: bagni multipli
      // Prodotti scelti dai listini fornitori (porte, piastrelle, sanitari…)
      // VANNO SALVATI sul preventivo per essere propagati al Computo Metrico della commessa.
      listini_selections: prev.listini_selections || [],
      // Infissi extra aggiunti dal modal (calcolo automatico)
      infissi_extras: prev.infissi_extras || [],
      // Pre-inclusi nel pacchetto (snapshot per tracciabilità futura)
      package_listini_items: pkg?.listini_items || [],
      // Flag override prezzo forfait (snapshot del pacchetto al momento del preventivo)
      package_price_override: pkg?.price_override ?? null,
      package_base_total: totals.base,
      note: prev.note,
      sconto_pct: prev.sconto_pct,
      scorporo_sconto_disponibile: Math.round(scorporo.totale * 100) / 100,
      iva_pct: prev.iva_pct,
      modalita_pagamento: prev.modalita_pagamento || null,
      totale_iva_incl: totals.total,
      totale_iva_escl: totals.subtotal - totals.sconto,
    };
    try {
      if (isNew || !numero) {
        const { data } = await api.post("/preventivi", payload);
        setNumero(data.numero);
        // Pulisci sessionStorage del configuratore (ora che il preventivo è salvato)
        try { sessionStorage.removeItem("preventivo_prefill"); } catch {}
        toast.success("Preventivo salvato");
        nav(`/preventivopacchetto/${data.id}`, { replace: true });
      } else {
        await api.put(`/preventivi/${id}`, payload);
        toast.success("Aggiornato");
      }
    } catch (e) {
      console.error("[PREVENTIVO] save error:", e);
      toast.error("Errore salvataggio");
    }
    setSaving(false);
  };

  const canNext = () => {
    if (step === 0) return !!prev.package_id;
    if (step === 1) return prev.mq > 0;
    if (step === 5) return (prev.cliente?.nome && prev.cliente?.cognome);
    return true;
  };

  const steps = ["Pacchetto", "Metri quadri", "Lavorazioni", "Optional", "Bagno", "Cliente", "Riepilogo"];

  if (loading) {
    return <div className="p-16 text-center mono text-zinc-500">caricamento…</div>;
  }

  const optFiltered = optionals.filter((o) => !prev.package_id || o.package_ids.includes(prev.package_id));

  return (
    <div className="min-h-screen bg-white" data-testid="preventivo-editor">
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => nav("/preventivi")} className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900" data-testid="back-to-preventivi">
            <ArrowLeft size={14} /> Preventivi
          </button>
          <div className="flex items-center gap-3">
            <div className="mono text-xs text-zinc-500">{numero || "nuovo"}</div>
            {numero && !isNew && (
              <select
                value={prev.stato || "bozza"}
                onChange={async (e) => {
                  const s = e.target.value;
                  if (s === prev.stato) return;
                  if (!window.confirm(`Cambiare stato preventivo a "${s}"?${s === "accettato" ? "\nVerrà generata automaticamente la commessa cantiere." : ""}`)) return;
                  try {
                    const { data } = await api.patch(`/preventivi/${id}/stato`, { stato: s });
                    setPrev((p) => ({ ...p, stato: s }));
                    toast.success(`Stato → ${s}${data.auto_commessa ? " · Commessa creata" : ""}`);
                  } catch (err) { toast.error(err?.response?.data?.detail || "Errore cambio stato"); }
                }}
                className={`h-9 text-xs px-2 rounded-sm border cursor-pointer font-semibold ${
                  prev.stato === "accettato" ? "bg-emerald-50 text-emerald-800 border-emerald-300" :
                  prev.stato === "rifiutato" ? "bg-rose-50 text-rose-800 border-rose-300" :
                  prev.stato === "inviato" ? "bg-blue-50 text-blue-800 border-blue-300" :
                  "bg-zinc-50 text-zinc-700 border-zinc-300"
                }`}
                data-testid="pkg-header-stato"
              >
                <option value="bozza">📝 Bozza</option>
                <option value="inviato">📤 Inviato</option>
                <option value="accettato">✅ Accettato</option>
                <option value="rifiutato">❌ Rifiutato</option>
              </select>
            )}
            {numero && !isNew && (
              <Button variant="outline" className="rounded-sm h-9 border-emerald-600 text-emerald-700 hover:bg-emerald-50" onClick={() => nav(`/preventivi/${id}/stampa`)} data-testid="open-stampa-btn">
                <FileText size={14} className="mr-2" /> Anteprima stampa
              </Button>
            )}
            <Button variant="outline" className="rounded-sm h-9" onClick={() => exportPDF(prev, pkg, totals, numero)} data-testid="pdf-preventivo-button">
              <Download size={14} className="mr-2" /> PDF rapido
            </Button>
            <Button className="rounded-sm h-9 bg-zinc-900 hover:bg-zinc-800" onClick={save} disabled={saving} data-testid="save-preventivo-button">
              <Save size={14} className="mr-2" /> Salva
            </Button>
          </div>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-0 border border-zinc-200 mb-8 overflow-x-auto">
          {steps.map((s, i) => (
            <button key={s} onClick={() => setStep(i)}
              className={`flex-1 min-w-[120px] px-4 py-3 text-xs uppercase tracking-widest text-left border-r border-zinc-200 last:border-r-0 transition-colors ${step === i ? "bg-zinc-900 text-white" : "text-zinc-500 hover:bg-zinc-50"}`}
              data-testid={`step-${i}`}
            >
              <div className="mono text-[10px] opacity-70 mb-1">{i + 1}</div>
              {s}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 min-w-0">
            {step === 0 && (
              <div>
                <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: "Outfit" }}>Scegli il pacchetto</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {packages.map((p) => (
                    <button key={p.id}
                      onClick={() => setPrev((s) => ({ ...s, package_id: p.id }))}
                      className={`text-left border p-6 transition-all hover:-translate-y-0.5 ${prev.package_id === p.id ? "border-zinc-900 bg-zinc-50" : "border-zinc-200"}`}
                      data-testid={`package-${p.id}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-2xl font-semibold" style={{ fontFamily: "Outfit", color: p.color }}>{p.name}</span>
                        <div className="mono text-sm text-zinc-500">€/m²</div>
                      </div>
                      <div className="mono text-3xl font-semibold text-zinc-900 mb-2">{fmtNum(p.price_per_m2, 0)}</div>
                      <div className="text-xs text-zinc-500 uppercase tracking-widest mb-3">{p.subtitle}</div>
                      <p className="text-sm text-zinc-700 leading-relaxed mb-4">{p.description}</p>
                      <div className="mono text-xs text-zinc-500">{p.items.length} lavorazioni incluse</div>
                      {prev.package_id === p.id && <div className="mt-3 text-xs text-zinc-900 mono flex items-center gap-1"><Check size={12} /> selezionato</div>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: "Outfit" }}>Superficie dell'abitazione</h2>
                <div className="max-w-md">
                  <Label className="label-kicker">Metri quadri</Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.5"
                    value={prev.mq}
                    onChange={(e) => setPrev((s) => ({ ...s, mq: Math.max(0, parseFloat(e.target.value) || 0) }))}
                    className="rounded-sm h-16 mono text-4xl mt-2"
                    data-testid="mq-input"
                  />
                  <div className="mono text-xs text-zinc-500 mt-2">La superficie calpestabile totale dell'immobile.</div>
                </div>
                {pkg && prev.mq > 0 && (
                  <div className="mt-8 border border-zinc-200 p-6 max-w-md" data-testid="preview-base">
                    <div className="label-kicker mb-2">Preview base</div>
                    {totals.hasOverride ? (
                      <>
                        <div className="mono text-3xl">{fmtEuro(Number(pkg.price_override))}</div>
                        <div className="text-xs text-zinc-500 mono mt-1">
                          {pkg.name} · <span className="text-amber-700 font-semibold">prezzo forfait pacchetto</span> (€/m² ignorato)
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mono text-3xl">{fmtEuro(pkg.price_per_m2 * mqAdjustment.mq_effettivi * mqAdjustment.multiplier)}</div>
                        <div className="text-xs text-zinc-500 mono mt-1">
                          {pkg.name} · {fmtNum(pkg.price_per_m2, 0)} €/m² × {fmtNum(mqAdjustment.mq_effettivi, 1)} m²
                          {mqAdjustment.multiplier !== 1 && <> · <span className="text-amber-700 font-semibold">×{mqAdjustment.multiplier.toFixed(2)} (maggiorazione mq piccole)</span></>}
                        </div>
                      </>
                    )}
                  </div>
                )}
                {/* Banner maggiorazione mq piccole — non si applica se override */}
                {prev.mq > 0 && !totals.hasOverride && mqAdjustment.mode !== "normal" && (
                  <div className={`mt-4 max-w-md border-l-4 p-4 text-sm ${mqAdjustment.mode === "a_corpo" ? "bg-rose-50 border-rose-500 text-rose-900" : "bg-amber-50 border-amber-500 text-amber-900"}`} data-testid="mq-adjustment-banner">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold uppercase text-xs tracking-widest mb-1">
                          {mqAdjustment.mode === "a_corpo" ? "Modalità A CORPO" : "Maggiorazione +10%"}
                        </div>
                        <div className="leading-relaxed">{mqAdjustment.note}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: "Outfit" }}>Lavorazioni incluse nel pacchetto</h2>
                {pkg && prev.mq > 0 && (
                  <div className="bg-emerald-50 border-2 border-emerald-300 p-5 mb-5 grid grid-cols-3 gap-4" data-testid="pacchetto-totale-mq">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-emerald-700">Pacchetto base</div>
                      <div className="mono text-2xl font-bold text-emerald-900">{fmtEuro(pkg.price_per_m2 * prev.mq)}</div>
                      <div className="text-[11px] mono text-emerald-700">{pkg.name} · {fmtNum(pkg.price_per_m2, 0)}€/m² × {prev.mq}m²</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-amber-700">+ Extra dal configuratore/infissi</div>
                      <div className="mono text-2xl font-bold text-amber-900" data-testid="pacchetto-extras-tot">{fmtEuro(totals?.extras || 0)}</div>
                      <div className="text-[11px] mono text-amber-700">solo le voci aggiuntive</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-700">Totale subtotale</div>
                      <div className="mono text-2xl font-bold text-zinc-900">{fmtEuro((pkg.price_per_m2 * prev.mq) + (totals?.extras || 0) + (totals?.optional || 0))}</div>
                      <div className="text-[11px] mono text-zinc-500">IVA esclusa, prima sconto</div>
                    </div>
                  </div>
                )}
                <p className="text-sm text-zinc-600 mb-4">Tutto questo è già <strong>incluso nel prezzo a m² del pacchetto {pkg?.name}</strong>. Se il cliente vuole una quantità superiore a quella inclusa, paga solo la differenza al prezzo del backoffice.</p>
                <div className="flex justify-end mb-3 gap-2">
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setExtraFreeOpen(true)} data-testid="add-extra-free-btn">+ Aggiungi extra libero</Button>
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setInfissiModalOpen(true)} data-testid="add-infissi-btn">+ Aggiungi infissi (extra)</Button>
                </div>
                {["DEMOLIZIONI", "MURATURA", "IMPIANTI", "INFISSI", "SERVIZI", "EXTRA"].map((cat) => {
                  const list = prev.items.filter((it) => {
                    const isDemo = /demoliz|smaltim|rimoz/i.test(it.name);
                    if (cat === "DEMOLIZIONI") return isDemo;
                    if (cat === "EXTRA") return it.category === "EXTRA";
                    return !isDemo && it.category === cat;
                  });
                  if (list.length === 0) return null;
                  const activeList = list.filter((it) => !it.excluded);
                  const excludedList = list.filter((it) => it.excluded);
                  const colorMap = { DEMOLIZIONI: "#DC2626", MURATURA: "#0F766E", IMPIANTI: "#2563EB", INFISSI: "#9333EA", SERVIZI: "#B45309", EXTRA: "#EA580C" };
                  const catLabel = cat === "EXTRA" ? "EXTRA · Configuratore Esigenze" : cat;
                  return (
                    <div key={cat} className="mb-6">
                      <div className="text-xs uppercase tracking-widest font-bold mb-2 flex items-center gap-2" style={{ color: colorMap[cat] }}>
                        {catLabel}
                        <span className="text-[10px] mono text-zinc-400 normal-case">{activeList.length} voci · {excludedList.length > 0 ? `${excludedList.length} rimosse` : ""}</span>
                      </div>
                      <table className="w-full text-sm border border-zinc-200">
                        <thead className="bg-zinc-50 text-xs uppercase tracking-widest text-zinc-500">
                          <tr>
                            <th className="w-8"></th>
                            <th className="text-left py-2 px-3 font-medium">Lavorazione</th>
                            <th className="text-right py-2 px-3 font-medium w-20">U.M.</th>
                            <th className="text-right py-2 px-3 font-medium w-28">Incluse</th>
                            <th className="text-right py-2 px-3 font-medium w-28">Richieste</th>
                            <th className="text-right py-2 px-3 font-medium w-28 hidden" data-testid="th-price">€ / unità</th>
                            <th className="text-right py-2 px-3 font-medium w-32">Extra a pagamento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeList.map((it) => {
                            const incl = it.included_qty || 0;
                            const reqs = Math.max(0, it.qty_richiesta || 0);
                            const unitPrice = Math.max(0, it.unit_price || 0);
                            // REGOLA: extra qty + extra prezzo soglia. Eccedenza prezzo SOLO se materiale modificabile.
                            const extraQty = incl === 0 ? reqs : Math.max(0, reqs - incl);
                            const extraQtyCost = extraQty * unitPrice;
                            const soglia = it.unit_price_pkg;
                            const isMaterialeMod = !!it.modificabile_dal_venditore;
                            const overSoglia = isMaterialeMod && soglia != null && soglia > 0 && unitPrice > soglia;
                            const extraPrezzoCost = overSoglia && incl > 0 ? (unitPrice - soglia) * incl : 0;
                            const extraCost = extraQtyCost + extraPrezzoCost;
                            return (
                              <tr key={it.id} className="border-t border-zinc-100" data-testid={`lav-row-${it.id}`}>
                                <td className="py-2 px-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPrev((s) => ({ ...s, items: s.items.map((x) => x.id === it.id ? { ...x, excluded: true } : x) }));
                                      toast.success(`"${it.name}" rimossa dal preventivo`);
                                    }}
                                    className="text-rose-600 hover:text-rose-800 hover:bg-rose-50 p-1 rounded"
                                    title="Rimuovi questa voce dal preventivo"
                                    data-testid={`lav-remove-${it.id}`}
                                  ><span className="text-xs">✕</span></button>
                                </td>
                                <td className="py-2 px-3">
                                  {it.name}
                                  {!it.from_configuratore && !it.is_extra_libero && (it.included_qty || 0) > 0 && (
                                    <SwapControl it={it} items={prev.items} vociMap={vociMap} isAdmin={vediDettagliMargine}
                                      onSwap={(alt) => { setPrev((s) => ({ ...s, items: s.items.map((x) => x.id === it.id ? swapItem(x, alt) : x) })); toast.success(`"${it.name}" sostituita con "${alt.name}"`); }}
                                      onRestore={() => setPrev((s) => ({ ...s, items: s.items.map((x) => x.id === it.id ? restoreItem(x) : x) }))} />
                                  )}
                                </td>
                                <td className="py-2 px-3 text-right mono text-xs text-zinc-500">{it.unit}</td>
                                <td className="py-2 px-3 text-right mono text-zinc-500">{fmtNum(it.included_qty, 2)}</td>
                                <td className="py-2 px-3 text-right">
                                  <Input type="number" min={0} step="0.01" value={it.qty_richiesta}
                                    onChange={(e) => {
                                      const v = Math.max(0, parseFloat(e.target.value) || 0);
                                      setPrev((s) => ({ ...s, items: s.items.map((x) => x.id === it.id ? { ...x, qty_richiesta: v } : x) }));
                                    }}
                                    className="rounded-sm h-7 text-right mono text-xs w-20 ml-auto"
                                    data-testid={`lav-qty-${it.id}`}
                                  />
                                </td>
                                <td className="hidden">
                                  {it.modificabile_dal_venditore ? (
                                    <Input type="number" min={0} step="0.01" value={it.unit_price}
                                      onChange={(e) => {
                                        const v = Math.max(0, parseFloat(e.target.value) || 0);
                                        setPrev((s) => ({ ...s, items: s.items.map((x) => x.id === it.id ? { ...x, unit_price: v } : x) }));
                                      }}
                                      className={`rounded-sm h-7 text-right mono text-xs w-20 ml-auto`}
                                      data-testid={`lav-price-${it.id}`}
                                    />
                                  ) : (
                                    <span>{fmtEuro(it.unit_price)}</span>
                                  )}
                                </td>
                                <td className={`py-2 px-3 text-right mono text-xs ${extraCost > 0 ? "text-orange-600 font-semibold" : "text-zinc-400"}`}>
                                  {extraCost > 0 ? (
                                    <div>
                                      {extraQty > 0 && <div>{`+${fmtNum(extraQty, 2)} ${it.unit} × ${fmtEuro(unitPrice)} = ${fmtEuro(extraQtyCost)}`}</div>}
                                      {overSoglia && incl > 0 && (
                                        <div className="text-amber-700">{`+${fmtEuro(unitPrice - soglia)}/${it.unit} (sopra soglia ${fmtEuro(soglia)}) × ${fmtNum(incl, 2)} = ${fmtEuro(extraPrezzoCost)}`}</div>
                                      )}
                                      <div className="font-bold mt-0.5">Tot. extra: {fmtEuro(extraCost)}</div>
                                    </div>
                                  ) : "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {excludedList.length > 0 && (
                        <div className="mt-2 bg-amber-50 border border-amber-300 p-2 text-[11px]" data-testid={`excluded-${cat}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="mono uppercase tracking-widest text-amber-800 text-[9px]">{excludedList.length} voci rimosse</span>
                            <button onClick={() => { const ids = new Set(excludedList.map((x) => x.id)); setPrev((s) => ({ ...s, items: s.items.map((x) => ids.has(x.id) ? { ...x, excluded: false } : x) })); }} className="text-amber-800 underline text-[10px]" data-testid={`restore-all-${cat}`}>Ripristina tutte</button>
                          </div>
                          <ul className="space-y-1 max-h-72 overflow-auto">
                            {excludedList.map((it) => (
                              <li key={it.id}>
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-amber-900 truncate">{it.name}</span>
                                  <button onClick={() => setPrev((s) => ({ ...s, items: s.items.map((x) => x.id === it.id ? { ...x, excluded: false } : x) }))} className="text-amber-700 hover:text-amber-900 text-[11px] underline" data-testid={`restore-${it.id}`}>ripristina</button>
                                </div>
                                <ExcludedSuggestions it={it} items={prev.items} vociMap={vociMap} isAdmin={vediDettagliMargine}
                                  onSwap={(alt) => { setPrev((s) => ({ ...s, items: s.items.map((x) => x.id === it.id ? swapItem(x, alt) : x) })); toast.success(`"${it.name}" sostituita con "${alt.name}"`); }} />
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
                <ScorporoBanner info={scorporo} subtotal={totals.subtotal} testId="scorporo-banner-lavorazioni" />
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: "Outfit" }}>Optional</h2>
                <p className="text-sm text-zinc-600 mb-6">Aggiunte non incluse nel pacchetto. Prezzi scontati applicando il pacchetto. La quantità è sempre modificabile.</p>
                <div className="space-y-3">
                  {optFiltered.map((o) => {
                    const selected = prev.optional.find((x) => x.id === o.id);
                    const isListinoScontato = o.tipo_prezzo === "listino_scontato";
                    // Prezzo unitario corrente (listino_scontato: live da backoffice; forfait: scontato dall'admin)
                    const unitPriceScontato = isListinoScontato
                      ? (o.prezzo_unitario_listino || 0) * (1 - (o.sconto_pct || 0) / 100)
                      : (o.per_m2 ? (o.unit_price_scontato || 0) : (o.price_scontato || 0));
                    const qty = selected?.qty ?? 0;
                    const total = selected ? (qty * unitPriceScontato) : 0;
                    const opUnit = o.unit || (o.per_m2 ? "m²" : "pz");
                    const hasExcludeExtras = (o.exclude_extras_categories || []).length > 0;
                    return (
                      <div key={o.id} className={`border p-4 flex items-start gap-4 ${selected ? "border-zinc-900 bg-zinc-50" : "border-zinc-200"}`} data-testid={`optional-${o.id}`}>
                        <Switch
                          checked={!!selected}
                          onCheckedChange={(v) => {
                            if (v) {
                              const defaultQty = isListinoScontato ? (o.unit === "m²" ? prev.mq : 0) : (o.per_m2 ? prev.mq : 1);
                              const t = defaultQty * unitPriceScontato;
                              setPrev((s) => ({ ...s, optional: [...s.optional, {
                                id: o.id, name: o.name,
                                qty: defaultQty, unit_price: unitPriceScontato, total: t,
                                per_m2: o.per_m2, unit: opUnit,
                                descrizione: o.descrizione || o.description || "",
                                // FLAG avanzati per ricalcolo corretto e exclude_extras
                                tipo_prezzo: o.tipo_prezzo || "forfait",
                                prezzo_unitario_listino: o.prezzo_unitario_listino,
                                sconto_pct: o.sconto_pct,
                                voce_backoffice_id: o.voce_backoffice_id,
                                exclude_extras_categories: o.exclude_extras_categories || [],
                              }] }));
                            } else {
                              setPrev((s) => ({ ...s, optional: s.optional.filter((x) => x.id !== o.id) }));
                            }
                          }}
                          data-testid={`optional-switch-${o.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium flex items-center gap-2">
                            {o.name}
                            {hasExcludeExtras && <span className="text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-bold uppercase">no-extra: {(o.exclude_extras_categories || []).join(", ")}</span>}
                            {isListinoScontato && <span className="text-[9px] px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded font-bold uppercase">listino live</span>}
                          </div>
                          {o.descrizione && <div className="text-[10px] text-zinc-500 mt-0.5">{o.descrizione}</div>}
                          <div className="mono text-xs text-zinc-500 mt-1">
                            {isListinoScontato ? (
                              <>Listino {fmtEuro(o.prezzo_unitario_listino || 0)}/{opUnit}
                                {(o.sconto_pct || 0) > 0 && <> · sconto {o.sconto_pct}% → <strong>{fmtEuro(unitPriceScontato)}/{opUnit}</strong></>}
                              </>
                            ) : (
                              <>Listino {fmtEuro(o.price_listino)} · Pacchetto {fmtEuro(o.price_scontato)}
                                {o.per_m2 && <> · {fmtEuro(o.unit_price_scontato)}/m²</>}
                                {!o.per_m2 && <> · {fmtEuro(o.price_scontato)}/{opUnit}</>}
                              </>
                            )}
                          </div>
                        </div>
                        {selected && (
                          <div className="flex items-center gap-2">
                            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Qty</Label>
                            <Input
                              type="number" min={0} step={(opUnit === "m²" || opUnit === "ml") ? "0.5" : "1"}
                              value={qty}
                              onChange={(e) => {
                                const v = Math.max(0, parseFloat(e.target.value) || 0);
                                const t = v * unitPriceScontato;
                                setPrev((s) => ({ ...s, optional: s.optional.map((x) => x.id === o.id ? { ...x, qty: v, total: t } : x) }));
                              }}
                              className="rounded-sm h-9 text-right mono w-24"
                              data-testid={`optional-qty-${o.id}`}
                            />
                            <span className="text-xs text-zinc-500 mono">{opUnit}</span>
                          </div>
                        )}
                        <div className="mono text-right font-medium min-w-[110px]">
                          {selected ? fmtEuro(total) : <span className="text-zinc-400">—</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Sezione Listini Fornitori (porte, piastrelle, sanitari, infissi) — il venditore sceglie i prodotti specifici per il cliente */}
                <div className="mt-8 border-t pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>🛒 Finiture da Listini Fornitori</h3>
                      <p className="text-xs text-zinc-500">Aggiungi porte, infissi, piastrelle, sanitari, ecc. dai listini caricati dall'admin. Il prezzo è già a rivendita.</p>
                    </div>
                    <Button size="sm" onClick={() => setListinoPickerOpen(true)} style={{ background: "var(--brand)", color: "white" }} data-testid="pack-listini-add"><Plus className="h-4 w-4 mr-1" />Aggiungi finiture</Button>
                  </div>

                  {/* Prodotti PRE-INCLUSI nel pacchetto (definiti dall'admin in AdminPacchetti) */}
                  {(pkg.listini_items || []).length > 0 && (
                    <div className="mb-4 border border-blue-200 bg-blue-50 rounded p-3" data-testid="pack-listini-included">
                      <div className="text-[11px] uppercase tracking-widest font-bold text-blue-900 mb-2">Finiture incluse nel pacchetto {pkg.name}</div>
                      <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase text-blue-700"><tr>
                          <th className="px-2 py-1 text-left">Prodotto</th>
                          <th className="px-2 py-1 text-left">Fornitore</th>
                          <th className="px-2 py-1 text-right w-16">Qty</th>
                          <th className="px-2 py-1 text-right w-24">€/u</th>
                          <th className="px-2 py-1 text-right w-24">Totale</th>
                          <th className="px-2 py-1 text-center w-20">Mod.</th>
                        </tr></thead>
                        <tbody>
                          {(pkg.listini_items || []).map((li, i) => {
                            // Cerca se il venditore ha già sostituito questo item (presente in listini_selections con _replaces_pkg_id)
                            const replacement = (prev.listini_selections || []).find(x => x._replaces_pkg_id === li.id);
                            const extra = replacement ? Math.max(0, (Number(replacement.prezzo_rivendita) || 0) - (Number(li.prezzo_rivendita) || 0)) * (Number(li.qty) || 1) : 0;
                            return (
                            <tr key={`pkg-li-${i}`} className="text-xs">
                              <td className="px-2 py-1">
                                <div className="font-medium">{li.nome}</div>
                                <div className="text-[10px] text-blue-700">{li.categoria}</div>
                                {replacement && (
                                  <div className="mt-1 text-[10px] bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">
                                    🔄 Sostituito con <strong>{replacement.nome}</strong> — Δ extra € {extra.toFixed(2)}
                                  </div>
                                )}
                              </td>
                              <td className="px-2 py-1">{li.fornitore_nome}</td>
                              <td className="px-2 py-1 text-right mono">{li.qty}</td>
                              <td className="px-2 py-1 text-right mono">€ {(li.prezzo_rivendita || 0).toFixed(2)}</td>
                              <td className="px-2 py-1 text-right mono font-bold">€ {((li.qty || 0) * (li.prezzo_rivendita || 0)).toFixed(2)}</td>
                              <td className="px-2 py-1 text-center">
                                {li.modificabile_dal_venditore ? (
                                  <button
                                    onClick={() => {
                                      // Apri dialog con prodotti dello stesso listino o stessa categoria
                                      const candidati = allProdottiFornitori.filter(p =>
                                        (p.listino_id === li.listino_id) || (p.categoria === li.categoria)
                                      ).filter(p => p.id !== li.id);
                                      setSubstituteDialog({ pkgItem: li, products: candidati });
                                    }}
                                    className="text-amber-700 hover:bg-amber-50 text-[10px] px-1.5 py-0.5 border border-amber-300 rounded"
                                    data-testid={`pkg-substitute-${i}`}
                                    title="Sostituisci con un altro prodotto dello stesso fornitore">
                                    ✏ Sostituisci
                                  </button>
                                ) : <span className="text-zinc-400" title="Bloccato dall'admin">🔒</span>}
                              </td>
                            </tr>
                            );
                          })}
                          <tr className="border-t border-blue-200 font-bold text-xs">
                            <td colSpan={4} className="px-2 py-1 text-right text-blue-900">Subtotale pre-incluso{totals.hasOverride ? " (già nel forfait)" : ""}:</td>
                            <td className="px-2 py-1 text-right mono text-blue-900" data-testid="pack-listini-included-tot">€ {(totals.listiniPkg || 0).toFixed(2)}</td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                      <p className="text-[10px] text-blue-700 mt-2">
                        ✏ = il venditore può sostituire/aggiornare il prodotto in fase di trattativa · 🔒 = bloccato dall'admin
                        {totals.hasOverride ? " · Il pacchetto ha prezzo forfait: questi prodotti sono già compresi nel totale." : ""}
                      </p>
                    </div>
                  )}

                  {(prev.listini_selections || []).length === 0 ? (
                    <div className="text-zinc-400 text-sm text-center py-6 border border-dashed border-zinc-300 rounded">Nessuna finitura da listino scelta. (Opzionale)</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50 text-xs uppercase text-zinc-500"><tr>
                        <th className="px-3 py-2 text-left">Prodotto</th>
                        <th className="px-3 py-2 text-left">Fornitore</th>
                        <th className="px-3 py-2 text-right w-20">Qty</th>
                        <th className="px-3 py-2 text-right w-28">€/u</th>
                        <th className="px-3 py-2 text-right w-28">Totale</th>
                        <th className="w-10"></th>
                      </tr></thead>
                      <tbody className="divide-y divide-zinc-100">
                        {(prev.listini_selections || []).map((p, i) => (
                          <tr key={`${p.listino_id}-${p.id}-${i}`}>
                            <td className="px-3 py-2"><div className="font-medium text-xs">{p.nome}</div><div className="text-[10px] text-zinc-500">{p.codice ? `[${p.codice}] ` : ""}{p.categoria_dettaglio || p.categoria}</div></td>
                            <td className="px-3 py-2 text-xs">{p.fornitore_nome}</td>
                            <td className="px-3 py-2"><Input type="number" min={0.01} step="0.01" value={p.qty} onChange={e => {
                              const q = parseFloat(e.target.value) || 0;
                              setPrev(s => ({ ...s, listini_selections: (s.listini_selections || []).map((x, j) => j === i ? { ...x, qty: q, importo: q * (x.prezzo_rivendita || 0) } : x) }));
                            }} className="h-8 text-xs text-right mono" data-testid={`pack-listino-qty-${i}`} /></td>
                            <td className="px-3 py-2 text-right mono">€ {(p.prezzo_rivendita || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right mono font-bold">€ {((p.qty || 0) * (p.prezzo_rivendita || 0)).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right"><button onClick={() => setPrev(s => ({ ...s, listini_selections: (s.listini_selections || []).filter((_, j) => j !== i) }))} className="text-rose-600" data-testid={`pack-listino-del-${i}`}>×</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: "Outfit" }}>Configurazione bagni</h2>
                <p className="text-sm text-zinc-600 mb-4">
                  Il pacchetto <strong>{pkg?.name || ""}</strong> include <strong>1 bagno SILVER</strong>.
                  Puoi <strong>cambiare il livello</strong> di quel bagno (paghi solo la differenza) o <strong>aggiungere altri bagni</strong> (paghi il prezzo intero del livello scelto).
                </p>

                {/* Inizializza primo bagno (incluso) se la lista è vuota — handled by useEffect below */}

                <div className="space-y-3" data-testid="bathrooms-list">
                  {(prev.bathrooms || []).map((b, idx) => {
                    const silverBase = bathroomTiers[0]?.price || 0;
                    const currentTier = bathroomTiers.find((t) => t.id === b.tier_id);
                    const cost = b.included
                      ? Math.max(0, (currentTier?.price || 0) - silverBase)
                      : (currentTier?.price || 0);
                    return (
                      <div key={b.id} className="border-2 border-zinc-200 p-4 bg-white" data-testid={`bath-row-${idx}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <div className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>Bagno #{idx + 1}</div>
                              {b.included ? (
                                <span className="text-[10px] mono uppercase tracking-widest bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">incluso nel pacchetto</span>
                              ) : (
                                <span className="text-[10px] mono uppercase tracking-widest bg-amber-100 text-amber-800 px-2 py-0.5 rounded">bagno extra</span>
                              )}
                            </div>
                            <div className="text-xs text-zinc-500 mt-0.5">
                              {b.included
                                ? "Paghi solo la differenza rispetto al SILVER incluso."
                                : "Paghi il prezzo intero del livello scelto (bagno aggiuntivo completo)."}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="mono text-2xl font-bold" style={{ color: currentTier?.color || "#0F172A" }}>
                              {cost > 0 ? `+ ${fmtEuro(cost)}` : "incluso"}
                            </div>
                            {!b.included && (
                              <button
                                onClick={() => setPrev((s) => ({ ...s, bathrooms: s.bathrooms.filter((x) => x.id !== b.id) }))}
                                className="text-xs text-rose-600 hover:text-rose-800 underline mt-1"
                                data-testid={`bath-remove-${idx}`}
                              >Rimuovi bagno</button>
                            )}
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-2">
                          {bathroomTiers.map((t) => (
                            <button
                              key={t.id}
                              onClick={() => setPrev((s) => ({ ...s, bathrooms: s.bathrooms.map((x) => x.id === b.id ? { ...x, tier_id: t.id } : x) }))}
                              className={`text-left border-2 p-3 transition-all hover:-translate-y-0.5 ${b.tier_id === t.id ? "border-zinc-900 bg-zinc-50" : "border-zinc-200"}`}
                              data-testid={`bath-${idx}-tier-${t.id}`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-base font-semibold" style={{ fontFamily: "Outfit", color: t.color }}>{t.name}</span>
                                {b.tier_id === t.id && <Check size={14} className="text-zinc-900" />}
                              </div>
                              <div className="text-[11px] text-zinc-600 leading-snug">{t.description}</div>
                              <div className="mt-2 mono text-[11px] text-zinc-500">
                                {b.included
                                  ? (t.id === bathroomTiers[0]?.id ? "incluso" : `+ ${fmtEuro((t.price || 0) - silverBase)} (upgrade)`)
                                  : `${fmtEuro(t.price || 0)} (intero)`
                                }
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  className="rounded-sm mt-4 h-10"
                  onClick={() => setPrev((s) => ({
                    ...s,
                    bathrooms: [...(s.bathrooms || []), { id: `bath-${Date.now()}`, tier_id: bathroomTiers[0]?.id, included: false }]
                  }))}
                  disabled={bathroomTiers.length === 0}
                  data-testid="add-bathroom-btn"
                >+ Aggiungi un altro bagno</Button>

                {/* Sintesi finale costi bagni */}
                <div className="mt-6 bg-zinc-50 border border-zinc-200 p-4">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Sintesi bagni</div>
                  <div className="space-y-1.5 text-sm">
                    {(prev.bathrooms || []).map((b, i) => {
                      const silverBase = bathroomTiers[0]?.price || 0;
                      const t = bathroomTiers.find((x) => x.id === b.tier_id);
                      const cost = b.included ? Math.max(0, (t?.price || 0) - silverBase) : (t?.price || 0);
                      return (
                        <div key={b.id} className="flex justify-between">
                          <span>Bagno #{i + 1} · {t?.name || "—"} {b.included ? "(incluso)" : "(extra)"}</span>
                          <span className="mono">{cost > 0 ? `+ ${fmtEuro(cost)}` : "incluso"}</span>
                        </div>
                      );
                    })}
                    <div className="flex justify-between pt-2 mt-2 border-t border-zinc-300 font-semibold">
                      <span>Totale aggiunte bagni</span>
                      <span className="mono" data-testid="bagni-total">{fmtEuro(totals.bagno)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 5 && (
              <div>
                <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: "Outfit" }}>Dati cliente</h2>
                <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
                  <div>
                    <Label className="label-kicker">Nome *</Label>
                    <Input value={prev.cliente?.nome || ""} onChange={(e) => setPrev((s) => ({ ...s, cliente: { ...s.cliente, nome: e.target.value } }))} className="rounded-sm h-10 mt-1" data-testid="cliente-nome" />
                  </div>
                  <div>
                    <Label className="label-kicker">Cognome *</Label>
                    <Input value={prev.cliente?.cognome || ""} onChange={(e) => setPrev((s) => ({ ...s, cliente: { ...s.cliente, cognome: e.target.value } }))} className="rounded-sm h-10 mt-1" data-testid="cliente-cognome" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="label-kicker">Indirizzo cantiere</Label>
                    <Input value={prev.cliente?.indirizzo || ""} onChange={(e) => setPrev((s) => ({ ...s, cliente: { ...s.cliente, indirizzo: e.target.value } }))} className="rounded-sm h-10 mt-1" />
                  </div>
                  <div>
                    <Label className="label-kicker">Email</Label>
                    <Input type="email" value={prev.cliente?.email || ""} onChange={(e) => setPrev((s) => ({ ...s, cliente: { ...s.cliente, email: e.target.value } }))} className="rounded-sm h-10 mt-1" />
                  </div>
                  <div>
                    <Label className="label-kicker">Telefono</Label>
                    <Input value={prev.cliente?.telefono || ""} onChange={(e) => setPrev((s) => ({ ...s, cliente: { ...s.cliente, telefono: e.target.value } }))} className="rounded-sm h-10 mt-1" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="label-kicker">Note</Label>
                    <Textarea value={prev.note || ""} onChange={(e) => setPrev((s) => ({ ...s, note: e.target.value }))} rows={4} className="rounded-sm mt-1" />
                  </div>
                  <div className="sm:col-span-2 border-t border-zinc-200 pt-4 mt-2">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <Label className="label-kicker">Sconto %</Label>
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          {isAdmin
                            ? "Admin: sconto libero senza limiti."
                            : `Venditore: fino al ${SOGLIA_SCONTO_AUTO}% applicabile direttamente. Oltre serve autorizzazione admin.`}
                        </div>
                      </div>
                      {scontoReq && scontoReq.stato === "pending" && (
                        <span className="text-[10px] mono uppercase tracking-widest bg-amber-100 text-amber-800 px-2 py-1 rounded flex items-center gap-1">
                          <Clock className="h-3 w-3" /> richiesta {scontoReq.pct_richiesto}% in attesa
                        </span>
                      )}
                      {scontoReq && scontoReq.stato === "approvato" && (
                        <span className="text-[10px] mono uppercase tracking-widest bg-emerald-100 text-emerald-800 px-2 py-1 rounded flex items-center gap-1">
                          <ShieldCheck className="h-3 w-3" /> {scontoReq.pct_approvato || scontoReq.pct_richiesto}% autorizzato
                        </span>
                      )}
                      {scontoReq && scontoReq.stato === "rifiutato" && (
                        <span className="text-[10px] mono uppercase tracking-widest bg-rose-100 text-rose-800 px-2 py-1 rounded">
                          {scontoReq.pct_richiesto}% rifiutato
                        </span>
                      )}
                    </div>
                    <div className="flex items-end gap-3">
                      <div className="flex-1 max-w-[180px]">
                        <Input
                          type="number" min={0} max={100} step="0.5"
                          value={prev.sconto_pct}
                          onChange={(e) => applySconto(parseFloat(e.target.value) || 0)}
                          className="rounded-sm h-10 mt-1 mono"
                          data-testid="sconto-input"
                        />
                      </div>
                      {!isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-sm h-10"
                          onClick={() => {
                            setScontoForm({ pct: Math.max(prev.sconto_pct, SOGLIA_SCONTO_AUTO + 1), motivo: scontoReq?.motivo || "" });
                            setScontoDialog(true);
                          }}
                          disabled={!numero || isNew}
                          data-testid="sconto-richiedi-btn"
                        >Richiedi sconto maggiore</Button>
                      )}
                    </div>
                    <ScorporoBanner info={scorporo} subtotal={totals.subtotal} onApply={applySconto} testId="scorporo-banner-sconto" />
                    {scontoReq && scontoReq.stato === "rifiutato" && scontoReq.admin_note && (
                      <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 p-2 rounded">
                        <strong>Admin:</strong> {scontoReq.admin_note}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="label-kicker">IVA %</Label>
                    <Input type="number" min={0} max={100} step="0.5" value={prev.iva_pct} onChange={(e) => setPrev((s) => ({ ...s, iva_pct: Math.max(0, Math.min(100, parseFloat(e.target.value) || 10)) }))} className="rounded-sm h-10 mt-1 mono" />
                  </div>
                </div>
              </div>
            )}

            {step === 6 && (
              <div>
                <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: "Outfit" }}>Riepilogo preventivo</h2>
                {((prev.items || []).some((it) => it.from_configuratore) || /Configuratore/i.test(prev.note || "")) && pkg && (
                  <div className="bg-amber-50 border border-amber-300 px-4 py-3 mb-4 flex items-center gap-3" data-testid="conforme-badge">
                    <Sparkles size={16} className="text-amber-700 flex-shrink-0" />
                    <div className="text-sm text-amber-900">
                      <strong>Conforme al pacchetto {pkg.name}</strong> scelto in fase di consulenza (Configuratore Esigenze)
                    </div>
                  </div>
                )}
                <div className="border border-zinc-200 p-6 space-y-5">
                  <div className="flex items-center justify-between pb-4 border-b border-zinc-100">
                    <div>
                      <div className="mono text-xs text-zinc-500">{numero || "—"}</div>
                      <div className="text-xl font-medium" style={{ fontFamily: "Outfit" }}>{(prev.cliente?.nome || "") + " " + (prev.cliente?.cognome || "")}</div>
                      <div className="text-sm text-zinc-500">{prev.cliente?.indirizzo}</div>
                    </div>
                    <div className="text-right">
                      <div className="mono text-xs text-zinc-500">Pacchetto</div>
                      <div className="text-xl font-semibold" style={{ fontFamily: "Outfit", color: pkg?.color }}>{pkg?.name}</div>
                      <div className="mono text-xs text-zinc-500">{prev.mq} m² · {fmtNum(pkg?.price_per_m2 || 0, 0)} €/m²</div>
                    </div>
                  </div>
                  <Row label="Base pacchetto" value={fmtEuro(totals.base)} />
                  {totals.extras > 0 && <Row label="Extra lavorazioni" value={fmtEuro(totals.extras)} />}
                  {totals.optional > 0 && <Row label="Optional" value={fmtEuro(totals.optional)} />}
                  {totals.listini > 0 && <Row label="🛒 Finiture (listini)" value={fmtEuro(totals.listini)} />}
                  {totals.bagno > 0 && <Row label={`Bagni aggiuntivi (${(prev.bathrooms || []).length || 1})`} value={fmtEuro(totals.bagno)} />}
                  {/* DETTAGLIO BAGNI MULTIPLI */}
                  {(prev.bathrooms || []).length > 0 && (
                    <div className="pt-2" data-testid="riepilogo-bagni">
                      <div className="label-kicker mb-2">Configurazione bagni</div>
                      <ul className="text-sm space-y-1.5">
                        {(prev.bathrooms || []).map((b, i) => {
                          const silverBase = bathroomTiers[0]?.price || 0;
                          const t = bathroomTiers.find((x) => x.id === b.tier_id);
                          const cost = b.included ? Math.max(0, (t?.price || 0) - silverBase) : (t?.price || 0);
                          return (
                            <li key={b.id} className="flex justify-between border-b border-zinc-100 pb-1">
                              <span>
                                <strong>Bagno #{i + 1}</strong> · {t?.name || "—"}
                                <span className="text-zinc-500 text-xs ml-1">{b.included ? "(incluso, paga differenza)" : "(extra completo)"}</span>
                                {t?.description && <div className="text-[11px] text-zinc-500">{t.description}</div>}
                              </span>
                              <span className="mono font-semibold">{cost > 0 ? fmtEuro(cost) : <span className="text-zinc-400">incluso</span>}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {/* DETTAGLIO OPTIONAL SELEZIONATI — usa prev.optional (lista reale) */}
                  {(prev.optional || []).length > 0 && (
                    <div className="pt-2" data-testid="riepilogo-optional">
                      <div className="label-kicker mb-2">Optional selezionati</div>
                      <ul className="text-sm space-y-1.5">
                        {(prev.optional || []).map((o) => (
                          <li key={o.id} className="flex justify-between border-b border-zinc-100 pb-1">
                            <span>
                              <strong>{o.name}</strong>
                              {o.qty != null && o.qty > 0 && <span className="text-zinc-500"> · {fmtNum(o.qty, 2)} {o.unit || ""}</span>}
                              {o.descrizione && <div className="text-xs text-zinc-500">{o.descrizione}</div>}
                            </span>
                            <span className="mono font-semibold">{fmtEuro(o.total || 0)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* DETTAGLIO VOCI: solo per preventivi a pacchetto NASCONDIAMO i prezzi unitari (è forfettario) */}
                  {(prev.items || []).filter(it => (it.qty || it.qty_richiesta) > 0).length > 0 && (
                    <div className="pt-2" data-testid="riepilogo-voci-table">
                      <div className="label-kicker mb-2">Lavorazioni incluse</div>
                      <p className="text-[10px] text-zinc-500 mb-2">Il prezzo del pacchetto è <strong>forfettario</strong> · queste sono le lavorazioni eseguite.</p>
                      <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase text-zinc-500 border-b border-zinc-200"><tr>
                          <th className="py-1.5 text-left">Lavorazione</th>
                          <th className="py-1.5 text-right w-20">Qty</th>
                          <th className="py-1.5 text-left w-16">U.M.</th>
                        </tr></thead>
                        <tbody className="divide-y divide-zinc-100">
                          {(prev.items || []).filter(it => (it.qty || it.qty_richiesta) > 0).map((it, i) => (
                            <tr key={i} className={it.from_configuratore ? "bg-amber-50/40" : (it.is_extra_libero ? "bg-blue-50/30" : "")}>
                              <td className="py-1.5">
                                <div className="font-medium">{it.name}{it.is_extra_libero && <span className="ml-2 text-[9px] uppercase bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">extra libero</span>}</div>
                                {it.category && <div className="text-[10px] uppercase text-zinc-500">{it.category}</div>}
                              </td>
                              <td className="py-1.5 text-right mono">{fmtNum(it.qty || it.qty_richiesta || 0, 2)}</td>
                              <td className="py-1.5 text-xs">{it.unit || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <Separator />
                  <Row label="Subtotale" value={fmtEuro(totals.subtotal)} bold />
                  {totals.sconto > 0 && <Row label={`Sconto ${prev.sconto_pct}%`} value={`- ${fmtEuro(totals.sconto)}`} />}
                  <Row label={`IVA ${prev.iva_pct}%`} value={fmtEuro(totals.iva)} />
                  <Separator />
                  <div className="flex items-baseline justify-between">
                    <div className="label-kicker">Totale finale</div>
                    <div className="mono text-4xl font-semibold" data-testid="preventivo-total">{fmtEuro(totals.total)}</div>
                  </div>
                  {/* Round 90 sexies: Modalità di pagamento OPZIONALE. Se compilata appare nel PDF. */}
                  <ModalitaPagamentoPicker prev={prev} setPrev={setPrev} totale={totals.total} />
                  <div className="text-[10px] text-zinc-500 italic mt-2 leading-snug" data-testid="pkg-pagamento-hint">
                    💡 <strong>Opzionale</strong>: senza rate qui verrà stampato il testo standard (rimando al contratto).
                    Le rate impostate compariranno nel PDF e saranno modificabili in <strong>Commessa → Allegato A</strong>.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right sticky summary */}
          <aside className="border border-zinc-200 p-5 h-fit sticky top-6">
            <div className="label-kicker mb-3">Totale preventivo</div>
            <div className="mono text-3xl font-semibold mb-4" data-testid="sidebar-total">{fmtEuro(totals.total)}</div>
            <div className="space-y-1.5 mono text-xs text-zinc-600 mb-5">
              <div className="flex justify-between"><span>Base</span><span>{fmtEuro(totals.base)}</span></div>
              <div className="flex justify-between"><span>Extra</span><span>{fmtEuro(totals.extras)}</span></div>
              <div className="flex justify-between"><span>Optional</span><span>{fmtEuro(totals.optional)}</span></div>
              {totals.listini > 0 && <div className="flex justify-between"><span>🛒 Finiture</span><span>{fmtEuro(totals.listini)}</span></div>}
              {totals.bagno > 0 && <div className="flex justify-between"><span>Bagno</span><span>{fmtEuro(totals.bagno)}</span></div>}
              <div className="flex justify-between"><span>IVA</span><span>{fmtEuro(totals.iva)}</span></div>
            </div>

            <MargineBox m={margine} details={vediDettagliMargine} breakdown={totals.costiBreakdown} testId="pacc-margine" />

            <div className="flex items-center gap-2 mb-3">
              <Button variant="outline" className="rounded-sm flex-1 h-9" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="prev-step">
                <ChevronLeft size={14} /> Indietro
              </Button>
              <Button className="rounded-sm flex-1 h-9 bg-zinc-900 hover:bg-zinc-800" onClick={() => setStep((s) => Math.min(6, s + 1))} disabled={!canNext() || step === 6} data-testid="next-step">
                Avanti <ChevronRight size={14} />
              </Button>
            </div>
            <Button className="rounded-sm w-full h-10 bg-zinc-900 hover:bg-zinc-800" onClick={save} disabled={saving} data-testid="finalize-button">
              <Save size={14} className="mr-2" /> {saving ? "Salvo…" : "Salva preventivo"}
            </Button>
          </aside>
        </div>
      </main>
      <InfissoQuickConfigurator open={infissiModalOpen} onClose={() => setInfissiModalOpen(false)} onConfirm={onInfissiConfirm} />
      <ListinoProdottoPicker
        open={listinoPickerOpen}
        onOpenChange={setListinoPickerOpen}
        onConfirm={(items) => {
          setPrev(s => {
            const cur = s.listini_selections || [];
            const out = [...cur];
            items.forEach(it => {
              const idx = out.findIndex(x => x.listino_id === it.listino_id && x.id === it.id);
              if (idx >= 0) out[idx] = { ...out[idx], qty: (parseFloat(out[idx].qty) || 0) + (parseFloat(it.qty) || 0) };
              else out.push(it);
            });
            return { ...s, listini_selections: out };
          });
          toast.success(`${items.length} prodotto/i aggiunto/i`);
        }}
      />
      {/* Dialog Sostituisci prodotto pacchetto */}
      <Dialog open={!!substituteDialog} onOpenChange={(o) => !o && setSubstituteDialog(null)}>
        <DialogContent className="max-w-2xl" data-testid="substitute-dialog">
          {substituteDialog && (
            <div className="space-y-3">
              <div>
                <h3 className="text-lg font-semibold">Sostituisci prodotto del pacchetto</h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Sostituisci <strong>{substituteDialog.pkgItem.nome}</strong> (€{Number(substituteDialog.pkgItem.prezzo_rivendita || 0).toFixed(2)}) con un altro prodotto dello stesso fornitore o categoria <strong>{substituteDialog.pkgItem.categoria}</strong>. La differenza viene contata come extra.
                </p>
              </div>
              <div className="max-h-[420px] overflow-y-auto border border-zinc-200 rounded">
                {(() => {
                  const grouped = {};
                  substituteDialog.products.forEach(p => { const k = p.fornitore_nome || p.listino_nome || "Senza fornitore"; (grouped[k] = grouped[k] || []).push(p); });
                  Object.values(grouped).forEach(arr => arr.sort((a, b) => (a.prezzo_rivendita || 0) - (b.prezzo_rivendita || 0)));
                  const pkgPrice = Number(substituteDialog.pkgItem.prezzo_rivendita) || 0;
                  const qty = Number(substituteDialog.pkgItem.qty) || 1;
                  return Object.keys(grouped).sort().map(forn => (
                    <div key={forn} className="border-b border-zinc-100 last:border-b-0">
                      <div className="px-3 py-1.5 bg-zinc-50 text-[10px] font-bold uppercase tracking-wider text-zinc-700">🏭 {forn} ({grouped[forn].length})</div>
                      {grouped[forn].map(p => {
                        const delta = (Number(p.prezzo_rivendita) || 0) - pkgPrice;
                        const totDelta = delta * qty;
                        return (
                          <button key={p.id} onClick={() => {
                            // Inserisci/sostituisci in listini_selections con tag _replaces_pkg_id
                            setPrev(s => {
                              const cur = (s.listini_selections || []).filter(x => x._replaces_pkg_id !== substituteDialog.pkgItem.id);
                              cur.push({ ...p, qty, _replaces_pkg_id: substituteDialog.pkgItem.id, _pkg_original_price: pkgPrice });
                              return { ...s, listini_selections: cur };
                            });
                            toast.success(`Sostituito: extra ${totDelta >= 0 ? "+" : ""}${totDelta.toFixed(2)}€`);
                            setSubstituteDialog(null);
                          }} className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b border-zinc-100 last:border-b-0 flex items-center justify-between gap-3" data-testid={`sub-pick-${p.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{p.codice ? `[${p.codice}] ` : ""}{p.nome}</div>
                              <div className="text-[10px] text-zinc-500">{p.descrizione || ""} · {p.unit || "pz"}</div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="mono text-sm font-bold">€ {Number(p.prezzo_rivendita || 0).toFixed(2)}</div>
                              <div className={`text-[10px] font-bold ${delta > 0 ? "text-rose-700" : delta < 0 ? "text-emerald-700" : "text-zinc-500"}`}>
                                {delta === 0 ? "= pacchetto" : delta > 0 ? `+€ ${totDelta.toFixed(2)} extra` : `−€ ${Math.abs(totDelta).toFixed(2)} risparmio`}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ));
                })()}
                {!substituteDialog.products.length && (
                  <div className="text-center py-6 text-sm text-zinc-500">Nessun prodotto alternativo disponibile per questa categoria/fornitore.</div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSubstituteDialog(null)}>Annulla</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={extraFreeOpen} onOpenChange={setExtraFreeOpen}>
        <DialogContent className="max-w-md" data-testid="extra-free-dialog">
          <div className="space-y-3">
            <div>
              <h3 className="text-lg font-semibold">Aggiungi voce extra libera</h3>
              <p className="text-xs text-zinc-500 mt-1">Aggiungi una qualsiasi lavorazione, materiale o servizio fuori pacchetto. Tutti i campi sono modificabili.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Descrizione</Label>
              <Input value={extraFreeForm.name} onChange={(e) => setExtraFreeForm(s => ({ ...s, name: e.target.value }))} placeholder="es. Carta da parati salone" className="mt-1" data-testid="extra-free-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Categoria</Label>
                <Select value={extraFreeForm.category} onValueChange={(v) => setExtraFreeForm(s => ({ ...s, category: v }))}>
                  <SelectTrigger className="mt-1" data-testid="extra-free-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DEMOLIZIONI">Demolizioni</SelectItem>
                    <SelectItem value="MURATURA">Muratura</SelectItem>
                    <SelectItem value="IMPIANTI">Impianti</SelectItem>
                    <SelectItem value="INFISSI">Infissi</SelectItem>
                    <SelectItem value="SERVIZI">Servizi</SelectItem>
                    <SelectItem value="EXTRA">Extra</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">U.M.</Label>
                <Input value={extraFreeForm.unit} onChange={(e) => setExtraFreeForm(s => ({ ...s, unit: e.target.value }))} placeholder="pz, m², ml" className="mt-1" data-testid="extra-free-unit" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Quantità</Label>
                <Input type="number" min={0} step="0.01" value={extraFreeForm.qty} onChange={(e) => setExtraFreeForm(s => ({ ...s, qty: parseFloat(e.target.value) || 0 }))} className="mt-1 mono" data-testid="extra-free-qty" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">€ / unità</Label>
                <Input type="number" min={0} step="0.01" value={extraFreeForm.unit_price} onChange={(e) => setExtraFreeForm(s => ({ ...s, unit_price: parseFloat(e.target.value) || 0 }))} className="mt-1 mono" data-testid="extra-free-price" />
              </div>
            </div>
            <div className="text-sm mono text-right text-zinc-700">Totale: <strong>{fmtEuro((extraFreeForm.qty || 0) * (extraFreeForm.unit_price || 0))}</strong></div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Note (facoltative)</Label>
              <Input value={extraFreeForm.note} onChange={(e) => setExtraFreeForm(s => ({ ...s, note: e.target.value }))} className="mt-1 text-xs" data-testid="extra-free-note" />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
              <Button variant="outline" size="sm" onClick={() => setExtraFreeOpen(false)}>Annulla</Button>
              <Button size="sm" disabled={!extraFreeForm.name || extraFreeForm.qty <= 0} onClick={() => {
                const newItem = {
                  id: `extra-${Date.now()}`,
                  voce_id: null,
                  name: extraFreeForm.name,
                  category: extraFreeForm.category,
                  unit: extraFreeForm.unit || "pz",
                  included_qty: 0,
                  qty_richiesta: extraFreeForm.qty,
                  unit_price: extraFreeForm.unit_price,
                  unit_price_pkg: 0,
                  modificabile_dal_venditore: true,
                  excluded: false,
                  is_extra_libero: true,
                  note: extraFreeForm.note,
                };
                setPrev(s => ({ ...s, items: [...s.items, newItem] }));
                setExtraFreeForm({ name: "", category: "EXTRA", unit: "pz", qty: 1, unit_price: 0, note: "" });
                setExtraFreeOpen(false);
              }} style={{ background: "var(--brand)", color: "white" }} data-testid="extra-free-save">Aggiungi al preventivo</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: richiesta sconto > 5% per autorizzazione admin */}
      <Dialog open={scontoDialog} onOpenChange={setScontoDialog}>
        <DialogContent className="max-w-md" data-testid="sconto-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-amber-600" /> Richiesta sconto maggiore del {SOGLIA_SCONTO_AUTO}%
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="bg-amber-50 border border-amber-200 p-3 rounded text-xs text-amber-900">
              Per applicare uno sconto superiore al {SOGLIA_SCONTO_AUTO}% serve l'autorizzazione dell'admin. La tua richiesta verrà inoltrata immediatamente. Riceverai una notifica appena viene presa una decisione.
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">% sconto richiesta</Label>
              <Input type="number" min={SOGLIA_SCONTO_AUTO + 0.5} max={100} step="0.5" value={scontoForm.pct} onChange={(e) => setScontoForm(s => ({ ...s, pct: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) }))} className="mono mt-1" data-testid="sconto-req-pct" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Motivazione (obbligatoria)</Label>
              <Textarea
                value={scontoForm.motivo}
                onChange={(e) => setScontoForm(s => ({ ...s, motivo: e.target.value }))}
                placeholder="Es: cliente in trattativa con concorrente, fidelizzazione, opportunità di referral, lavori extra futuri..."
                rows={4}
                className="mt-1"
                data-testid="sconto-req-motivo"
              />
            </div>
            {scontoReq && scontoReq.stato === "pending" && (
              <div className="text-xs text-zinc-500">
                Esiste già una richiesta pending del <strong>{scontoReq.pct_richiesto}%</strong>. Inviando ne crei una aggiornata che la sostituisce.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScontoDialog(false)} data-testid="sconto-req-cancel">Annulla</Button>
            <Button
              style={{ background: "var(--brand)", color: "white" }}
              disabled={scontoForm.pct <= SOGLIA_SCONTO_AUTO || !scontoForm.motivo.trim() || !numero}
              onClick={async () => {
                try {
                  const { data } = await api.post(`/preventivi/${id}/sconto-richiesta`, { pct: scontoForm.pct, motivo: scontoForm.motivo.trim() });
                  setScontoReq(data);
                  setScontoDialog(false);
                  toast.success(`Richiesta inviata all'admin (${data.pct_richiesto}%)`);
                } catch (e) {
                  toast.error("Errore: " + (e?.response?.data?.detail || e.message));
                }
              }}
              data-testid="sconto-req-send"
            >Invia richiesta all'admin</Button>
          </DialogFooter>
          {!numero && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded-sm" data-testid="sconto-req-save-hint">Salva prima il preventivo per poter inviare la richiesta di sconto all'admin.</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, bold = false }) {
  return (
    <div className="flex items-baseline justify-between">
      <div className={`${bold ? "font-medium" : "text-zinc-600"} text-sm`}>{label}</div>
      <div className={`mono ${bold ? "font-semibold" : ""}`}>{value}</div>
    </div>
  );
}

function exportPDF(prev, pkg, totals, numero) {
  if (!pkg) { toast.error("Seleziona un pacchetto"); return; }
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  let y = 18;
  doc.setFont("helvetica", "bold"); doc.setFontSize(22);
  doc.text("Preventivo Ristrutturazione", 18, y); y += 7;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(100);
  doc.text(`${numero || ""} · ${new Date().toLocaleDateString("it-IT")}`, 18, y); y += 8;
  doc.setTextColor(0); doc.setLineWidth(0.3); doc.line(18, y, W - 18, y); y += 6;

  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text(`Cliente: ${prev.cliente?.nome || ""} ${prev.cliente?.cognome || ""}`, 18, y); y += 5;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(80);
  if (prev.cliente?.indirizzo) { doc.text(prev.cliente.indirizzo, 18, y); y += 4; }
  if (prev.cliente?.email) { doc.text(prev.cliente.email, 18, y); y += 4; }
  doc.setTextColor(0);
  y += 4;

  // Badge "Conforme al pacchetto X scelto in fase di consulenza" (se origina dal configuratore)
  const fromConfiguratore = (prev.items || []).some((it) => it.from_configuratore) || /Configuratore/i.test(prev.note || "");
  if (fromConfiguratore && pkg) {
    doc.setFillColor(254, 243, 199); // amber-50
    doc.setDrawColor(217, 119, 6); // amber-600
    doc.setLineWidth(0.4);
    doc.rect(18, y, W - 36, 10, "FD");
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(146, 64, 14); // amber-800
    doc.text(`Conforme al pacchetto ${pkg.name} scelto in fase di consulenza (Configuratore Esigenze)`, 22, y + 6.5);
    doc.setTextColor(0);
    y += 14;
  }

  doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  doc.text(`Pacchetto ${pkg.name}`, 18, y);
  doc.text(`${prev.mq} m² × ${fmtNum(pkg.price_per_m2, 0)} €/m²`, W - 18, y, { align: "right" });
  y += 7;

  doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  doc.setFillColor(244, 244, 245); doc.rect(18, y - 4, W - 36, 6, "F");
  doc.text("Descrizione", 20, y);
  doc.text("Q.ta", 110, y, { align: "right" });
  doc.text("€ unit.", 140, y, { align: "right" });
  doc.text("Totale", W - 20, y, { align: "right" });
  y += 6;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  // Base
  doc.text("Base pacchetto", 20, y);
  doc.text(`${prev.mq}`, 110, y, { align: "right" });
  doc.text(`${fmtNum(pkg.price_per_m2, 0)} €`, 140, y, { align: "right" });
  doc.text(fmtEuro(totals.base), W - 20, y, { align: "right" });
  y += 5;

  // Extras (allineato alla regola: extra solo se qty > incluse oppure incluse===0)
  (prev.items || []).forEach((it) => {
    if (it.excluded) return;
    const incl = it.included_qty || 0;
    const reqs = Math.max(0, it.qty_richiesta || 0);
    const extra = incl === 0 ? reqs : Math.max(0, reqs - incl);
    if (extra <= 0) return;
    const cost = extra * (Math.max(0, it.unit_price || 0));
    if (y > 275) { doc.addPage(); y = 20; }
    doc.text(`Extra ${it.name}`, 20, y);
    doc.text(fmtNum(extra, 2), 110, y, { align: "right" });
    doc.text(fmtEuro(it.unit_price), 140, y, { align: "right" });
    doc.text(fmtEuro(cost), W - 20, y, { align: "right" });
    y += 5;
  });
  // Optional
  (prev.optional || []).forEach((o) => {
    if (y > 275) { doc.addPage(); y = 20; }
    doc.text(o.name, 20, y);
    doc.text(`${fmtNum(o.qty || 1, 2)} ${o.unit || ""}`, 110, y, { align: "right" });
    if (o.unit_price) doc.text(fmtEuro(o.unit_price), 140, y, { align: "right" });
    doc.text(fmtEuro(o.total || 0), W - 20, y, { align: "right" });
    y += 5;
  });
  // Bagni multipli
  const silverBasePDF = 0; // Non disponibile qui senza tiers; usiamo total da prev.bathrooms.cost
  if ((prev.bathrooms || []).length > 0) {
    (prev.bathrooms || []).forEach((b, i) => {
      if (y > 275) { doc.addPage(); y = 20; }
      const label = `Bagno #${i + 1} · ${b.tier_id || ""} ${b.included ? "(incluso · paga differenza)" : "(extra completo)"}`;
      // Recupera costo dal computo principale (totals.bagno) non disponibile per voce — passiamo solo etichetta
      doc.text(label, 20, y);
      y += 5;
    });
    if (totals.bagno > 0) {
      doc.setFont("helvetica", "bold");
      doc.text("Totale bagni aggiuntivi", 140, y, { align: "right" });
      doc.text(fmtEuro(totals.bagno), W - 20, y, { align: "right" });
      doc.setFont("helvetica", "normal");
      y += 5;
    }
  } else if (totals.bagno > 0) {
    if (y > 275) { doc.addPage(); y = 20; }
    doc.text(`Bagno ${prev.bathroom_tier || ""}`, 20, y);
    doc.text(fmtEuro(totals.bagno), W - 20, y, { align: "right" });
    y += 5;
  }

  y += 4;
  doc.line(18, y, W - 18, y); y += 6;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.text("Subtotale", 140, y, { align: "right" });
  doc.text(fmtEuro(totals.subtotal), W - 20, y, { align: "right" }); y += 5;
  if (totals.sconto > 0) {
    doc.text(`Sconto ${prev.sconto_pct}%`, 140, y, { align: "right" });
    doc.text(`- ${fmtEuro(totals.sconto)}`, W - 20, y, { align: "right" }); y += 5;
  }
  doc.text(`IVA ${prev.iva_pct}%`, 140, y, { align: "right" });
  doc.text(fmtEuro(totals.iva), W - 20, y, { align: "right" }); y += 6;

  doc.line(18, y, W - 18, y); y += 7;
  doc.setFont("helvetica", "bold"); doc.setFontSize(16);
  doc.text("TOTALE", 20, y);
  doc.text(fmtEuro(totals.total), W - 20, y, { align: "right" });

  if (prev.note) {
    y += 15;
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.text("Note:", 18, y); y += 5;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    const lines = doc.splitTextToSize(prev.note, W - 36);
    doc.text(lines, 18, y);
  }

  doc.save(`${numero || "preventivo"}.pdf`);
}
